'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { PRODUCT_CONFIG } from '@eliadmin/core/src/constants/product-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';

export type ProductPriceProps = {
  product: Product;
  className?: {
    main?: string;
    discount?: string;
    discountWrapper?: string;
    price?: string;
    priceAfterDiscount?: string;
    priceBeforeDiscount?: string;
  };
};

export function ProductPrice({ product, className }: ProductPriceProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const currency = generalLayout.currency;
  const currencySettings = generalLayout.currency_settings;
  const { t } = useTranslations();

  const me = useCustomerStore(state => state.me);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });
  const discount = priceDisplay.showPreviousPrice ? priceDisplay.discount : 0;
  const priceBeforeDiscount = priceDisplay.beforeDiscount;
  const priceAfterDiscount = priceDisplay.current;
  const priceAfterDiscount2 = priceDisplay.secondary;

  const unit = templateSettings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';

  const isDiscounted = discount > 0 && priceAfterDiscount !== priceBeforeDiscount;

  if (priceDisplay.priceOnRequest) return null;

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-between gap-1',
        'ea-product-price',
        className?.main,
      )}
    >
      <div className="flex flex-col items-center">
        {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
          <p className="whitespace-nowrap text-sm text-[#6E6D7B]">
            {`${t('text1926')} ${formatPrice(
              priceDisplay.mocPrice,
              currency,
              currencySettings,
            )}${unit}`}
          </p>
        ) : null}
        <div
          className={cn(
            'flex flex-wrap items-center gap-2',
            'ea-product-price__price',
            className?.price,
          )}
        >
          <p
            className={cn(
              'h5',
              'whitespace-nowrap font-semibold',
              'ea-product-price__price-after-discount',
              isDiscounted && 'text-[#EA2A30]',
              className?.priceAfterDiscount,
            )}
          >
            {formatPrice(priceAfterDiscount, currency, currencySettings)} {unit}
          </p>
          {discount ? (
            <del
              className={cn(
                'text-xs font-normal text-[#6E6D7B] sm:text-sm',
                'ea-product-price__price-before-discount',
                className?.priceBeforeDiscount,
              )}
            >{`${formatPrice(priceBeforeDiscount, currency, currencySettings)}${unit}`}</del>
          ) : null}
          <Paragraph color="neutral" size="sm" className="hidden text-[#6E6D7B] sm:block">
            {priceDisplay.secondaryIncludesVat ? t('text443') : t('text1122')}
          </Paragraph>
        </div>
        {templateSettings?.product_teaser?.price_without_vat?.enabled && (
          <p>
            {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
            {formatPrice(priceAfterDiscount2, currency, currencySettings)} {unit}{' '}
            {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
          </p>
        )}
      </div>
    </div>
  );
}
